import { computed, signal } from '@preact/signals-react';
import {
  HTTPStatusError,
  isAgentDBMigrationTransientError,
  type WebAgentCapabilityRequest,
} from '@/website/lib/chat-api';
import { normalizeText } from './conversation-utils';
import type { ConversationCoreTransport } from './types';

function isPendingCapabilityRequest(request: WebAgentCapabilityRequest) {
  return normalizeText(request.status)?.toLowerCase() === 'pending';
}

function sortCapabilityRequests(requests: WebAgentCapabilityRequest[]) {
  return [...requests].sort((a, b) => {
    const updatedAtDelta = b.updatedAt - a.updatedAt;
    if (updatedAtDelta !== 0) return updatedAtDelta;
    return b.requestId.localeCompare(a.requestId);
  });
}

function agentDBRetryDelayMs(error: unknown): number | null {
  if (!isAgentDBMigrationTransientError(error)) return null;
  const retryAfterSeconds =
    error instanceof HTTPStatusError ? error.retryAfterSeconds : null;
  return Math.max(1000, (retryAfterSeconds ?? 5) * 1000);
}

export class CapabilityRequestLiveDataset {
  readonly agentGroupId = signal<string | null>(null);
  readonly requestsById = signal(new Map<string, WebAgentCapabilityRequest>());
  readonly refreshing = signal(false);
  readonly loaded = signal(false);
  readonly lastError = signal<unknown | null>(null);
  readonly openCount = signal(0);

  readonly pendingRequests = computed(() =>
    sortCapabilityRequests(
      [...this.requestsById.value.values()].filter(isPendingCapabilityRequest)
    )
  );

  private unsubscribe: (() => void) | null = null;
  private subscribedAgentGroupId: string | null = null;
  private transport: ConversationCoreTransport | null = null;
  private pendingRefreshPromise: Promise<WebAgentCapabilityRequest[]> | null =
    null;
  private migrationRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private mutationSequence = 0;
  private latestRequestSequenceById = new Map<string, number>();
  private latestRequestUpdatedAtById = new Map<string, number>();
  private latestRequestStatusById = new Map<string, string>();

  constructor(transport?: ConversationCoreTransport) {
    if (transport) {
      this.configure(transport);
    }
  }

  configure(transport: ConversationCoreTransport) {
    this.stop();
    this.transport = transport;
  }

  private requireTransport() {
    if (!this.transport) {
      throw new Error(
        'CapabilityRequestLiveDataset transport is not configured.'
      );
    }
    return this.transport;
  }

  setAgentGroup(agentGroupId: string | null | undefined) {
    const normalizedAgentGroupId = normalizeText(agentGroupId);
    if (this.agentGroupId.peek() === normalizedAgentGroupId) return false;
    this.stop();
    this.agentGroupId.value = normalizedAgentGroupId;
    this.reset();
    return true;
  }

  start() {
    const agentGroupId = this.agentGroupId.value;
    if (!agentGroupId) {
      this.stop();
      return;
    }
    if (this.subscribedAgentGroupId === agentGroupId) return;

    const transport = this.requireTransport();
    this.stop();
    this.subscribedAgentGroupId = agentGroupId;
    this.unsubscribe = transport.subscribeCapabilityRequests({
      agentGroupId,
      onOpen: () => {
        this.openCount.value += 1;
        void this.refreshPending().catch(error => {
          this.lastError.value = error;
        });
      },
      onRequest: request => this.apply(request),
      onError: error => {
        this.lastError.value = error;
      },
    });
  }

  stop() {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.subscribedAgentGroupId = null;
    this.clearMigrationRetry();
  }

  async refreshPending() {
    const agentGroupId = this.agentGroupId.value;
    if (!agentGroupId) return [];
    if (this.pendingRefreshPromise) return this.pendingRefreshPromise;

    const transport = this.requireTransport();
    const refreshStartSequence = this.mutationSequence;
    this.refreshing.value = true;
    this.lastError.value = null;
    const refreshPromise = transport
      .listCapabilityRequests(agentGroupId, {
        status: 'pending',
        limit: 100,
      })
      .then(requests => {
        this.replacePending(requests, refreshStartSequence);
        this.loaded.value = true;
        return this.pendingRequests.value;
      })
      .catch(error => {
        this.lastError.value = error;
        this.scheduleMigrationRetry(error);
        throw error;
      })
      .finally(() => {
        if (this.pendingRefreshPromise === refreshPromise) {
          this.pendingRefreshPromise = null;
          this.refreshing.value = false;
        }
      });
    this.pendingRefreshPromise = refreshPromise;
    return refreshPromise;
  }

  replacePending(
    requests: WebAgentCapabilityRequest[],
    refreshStartSequence = this.mutationSequence
  ) {
    const next = new Map<string, WebAgentCapabilityRequest>();
    for (const request of requests) {
      const requestId = normalizeText(request.requestId);
      if (!requestId || !isPendingCapabilityRequest(request)) continue;
      if (
        this.hasNewerKnownRequest(requestId, request, {
          keepKnownStatusOnEqualTimestamp: true,
        })
      ) {
        continue;
      }
      next.set(requestId, request);
    }
    for (const [requestId, request] of this.requestsById.value) {
      if (
        (this.latestRequestSequenceById.get(requestId) ?? 0) >
        refreshStartSequence
      ) {
        next.set(requestId, request);
      }
    }
    this.requestsById.value = next;
  }

  apply(request: WebAgentCapabilityRequest | null | undefined) {
    const requestId = normalizeText(request?.requestId);
    if (!request || !requestId) return;
    if (this.hasNewerKnownRequest(requestId, request)) return;

    this.mutationSequence += 1;
    this.latestRequestSequenceById.set(requestId, this.mutationSequence);
    this.latestRequestUpdatedAtById.set(requestId, request.updatedAt);
    this.latestRequestStatusById.set(
      requestId,
      normalizeText(request.status)?.toLowerCase() ?? ''
    );

    const next = new Map(this.requestsById.value);
    if (isPendingCapabilityRequest(request)) {
      next.set(requestId, request);
    } else {
      next.delete(requestId);
    }
    this.requestsById.value = next;
    this.loaded.value = true;
  }

  reset() {
    this.clearMigrationRetry();
    this.requestsById.value = new Map();
    this.refreshing.value = false;
    this.loaded.value = false;
    this.lastError.value = null;
    this.openCount.value = 0;
    this.pendingRefreshPromise = null;
    this.mutationSequence = 0;
    this.latestRequestSequenceById.clear();
    this.latestRequestUpdatedAtById.clear();
    this.latestRequestStatusById.clear();
  }

  private hasNewerKnownRequest(
    requestId: string,
    request: WebAgentCapabilityRequest,
    options: { keepKnownStatusOnEqualTimestamp?: boolean } = {}
  ) {
    const latestUpdatedAt = this.latestRequestUpdatedAtById.get(requestId);
    if (latestUpdatedAt === undefined) return false;
    if (latestUpdatedAt > request.updatedAt) return true;
    if (latestUpdatedAt < request.updatedAt) return false;
    if (!options.keepKnownStatusOnEqualTimestamp) return false;
    const latestStatus = this.latestRequestStatusById.get(requestId);
    return (
      latestStatus !== undefined &&
      latestStatus !== (normalizeText(request.status)?.toLowerCase() ?? '')
    );
  }

  private scheduleMigrationRetry(error: unknown) {
    const delay = agentDBRetryDelayMs(error);
    if (delay === null || this.migrationRetryTimer) return;
    this.migrationRetryTimer = setTimeout(() => {
      this.migrationRetryTimer = null;
      void this.refreshPending().catch(retryError => {
        this.lastError.value = retryError;
      });
    }, delay);
  }

  private clearMigrationRetry() {
    if (!this.migrationRetryTimer) return;
    clearTimeout(this.migrationRetryTimer);
    this.migrationRetryTimer = null;
  }
}
